"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddRoleOffcanvas } from "./add"
import { DeleteRoleModal } from "./delete"
import { EditRoleOffcanvas } from "./edit"
import { RolesList } from "./list"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"

interface Permission {
  id: number
  permission_id?: number
  name: string
  description: string
  resource: string
  action?: string
}

interface RolePermission {
  permission_id: number
  permission: Permission
}

interface Role {
  id: number
  name: string
  description: string
  level: number
  reserved: boolean
  created_at: string
  updated_at: string
  role_permissions?: RolePermission[]
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface RoleFormData {
  name: string
  description: string
  permissions: number[]
}

interface EditRoleFormData extends RoleFormData {
  id: number
}

export const RolesManagement: React.FC = () => {
  const { fetchProfile } = useAuth()

  // State management
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({})
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })

  // Loading states
  const [rolesLoading, setRolesLoading] = useState(false)
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Modal/Offcanvas states
  const [showAddRole, setShowAddRole] = useState(false)
  const [showEditRole, setShowEditRole] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)

  // Search state
  const [searchTerm, setSearchTerm] = useState("")

  // API Functions
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })

  // Debounced search — API fires only after 400 ms of inactivity
  const debouncedSearch = useDebounce(searchTerm, 400)

  const fetchRoles = async (search = "", page = 1, limitValue?: number, sort = sortConfig) => {
    setRolesLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      const response = await api.get("/roles/list", {
        params: queryParams,
      })

      if (response.data.success) {
        setRoles(response.data.data.roles || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching roles:", error)
    } finally {
      setRolesLoading(false)
    }
  }

  const fetchPermissions = async () => {
    setPermissionsLoading(true)
    try {
      const response = await api.get("/roles/permissions")

      if (response.data.success) {
        const permissionsData = response.data.data || {}
        const validPermissions: Record<string, Permission[]> = {}

        const excludedResources = ['requirement', 'Project Requirement', 'Gallery', 'GalleryUpload']

        if (typeof permissionsData === 'object' && !Array.isArray(permissionsData)) {
          Object.entries(permissionsData).forEach(([key, value]) => {
            if (excludedResources.includes(key)) return

            if (Array.isArray(value)) {
              validPermissions[key] = value.map((perm: any) => ({
                id: perm.id || perm.permission_id,
                permission_id: perm.permission_id || perm.id,
                name: perm.name,
                description: perm.description,
                resource: perm.resource,
                action: perm.action,
              }))
            }
          })
        }

        // Deduplicate each group by resource+action
        Object.keys(validPermissions).forEach(key => {
          const seen = new Set<string>()
          validPermissions[key] = validPermissions[key].filter(p => {
            const sig = `${p.resource}::${p.action}`
            if (seen.has(sig)) return false
            seen.add(sig)
            return true
          })
        })

        setPermissions(validPermissions)
      }
    } catch (error) {
      console.error("Error fetching permissions:", error)
    } finally {
      setPermissionsLoading(false)
    }
  }

  const fetchRoleById = async (roleId: number): Promise<Role | null> => {
    try {
      const response = await api.get(`/roles/${roleId}`)

      if (response.data.success) {
        return response.data.data
      }
      return null
    } catch (error) {
      console.error("Error fetching role by ID:", error)
      return null
    }
  }

  const createRole = async (roleData: RoleFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/roles/create", roleData)

      if (response.data.success) {
        await fetchRoles()
        setShowAddRole(false)
        console.log("Role created successfully")
      } else {
        console.error("Failed to create role:", response.data.message)
      }
    } catch (error) {
      console.error("Error creating role:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateRole = async (roleData: EditRoleFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put("/roles/update", roleData)

      if (response.data.success) {
        await fetchRoles()
        setShowEditRole(false)
        setSelectedRole(null)
        // Refresh current user's permissions immediately so changes take effect without re-login
        await fetchProfile()
      } else {
        console.error("Failed to update role:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating role:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const deleteRole = async () => {
    if (!selectedRole) return

    setActionLoading(true)
    try {
      const response = await api.delete("/roles/delete", {
        data: { id: selectedRole.id },
      })

      if (response.data.success) {
        await fetchRoles()
        setShowDeleteModal(false)
        setSelectedRole(null)
        console.log("Role deleted successfully")
      } else {
        console.error("Failed to delete role:", response.data.message)
      }
    } catch (error) {
      console.error("Error deleting role:", error)
    } finally {
      setActionLoading(false)
    }
  }

  // Event handlers
  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // Actual API call is triggered by the debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchRoles(debouncedSearch, 1, undefined, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handlePageChange = (page: number) => {
    fetchRoles(searchTerm, page, undefined, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchRoles(searchTerm, 1, limit, sortConfig)
  }

  const handleAddRole = () => {
    setShowAddRole(true)
  }

  const handleEditRole = async (role: Role) => {
    // Fetch full role details with permissions
    const fullRole = await fetchRoleById(role.id)
    if (fullRole) {
      setSelectedRole(fullRole)
      setShowEditRole(true)
    }
  }

  const handleDeleteRole = (role: Role) => {
    setSelectedRole(role)
    setShowDeleteModal(true)
  }

  const handleCloseModals = () => {
    setShowAddRole(false)
    setShowEditRole(false)
    setShowDeleteModal(false)
    setSelectedRole(null)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchRoles(searchTerm, pagination.page, undefined, sort)
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchRoles()
    fetchPermissions()
  }, [])

  return (
    <div className="">
      <div className="">
        {/* Main Roles List */}
        <RolesList
          roles={roles}
          pagination={pagination}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onAddRole={handleAddRole}
          onEditRole={handleEditRole}
          onDeleteRole={handleDeleteRole}
          onSort={handleSort}
          loading={rolesLoading}
          searchValue={searchTerm}
          sortState={sortConfig}
        />

        {/* Add Role Offcanvas */}
        <AddRoleOffcanvas
          isOpen={showAddRole}
          onClose={handleCloseModals}
          onSubmit={createRole}
          permissions={permissions as any}
          loading={actionLoading}
        />

        {/* Edit Role Offcanvas */}
        <EditRoleOffcanvas
          isOpen={showEditRole}
          onClose={handleCloseModals}
          onSubmit={updateRole}
          role={selectedRole as any}
          permissions={permissions as any}
          loading={actionLoading}
        />

        {/* Delete Role Modal */}
        <DeleteRoleModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onDelete={deleteRole}
          text={
            selectedRole
              ? `Are you sure you want to delete the role "${selectedRole.name}"? This will permanently remove the role and all its associated permissions from the system.`
              : ""
          }
          loading={actionLoading}
        />
      </div>
    </div>
  )
}
